import { StarFilled, StarOutlined } from '@ant-design/icons';
import FilterChip from 'components/buttons/FilterChip';
import AskMidwifeCard from 'components/info/AskMidwifeCard';
import { CategoryIconBox } from 'components/info/CategoryIcon';
import CategoryPageItems from 'components/info/CategoryPageItems';
import { useBookmarkCategory, useCategoryPage } from 'components/info/queries';
import { Page } from 'components/layout/page';
import PageTopBar from 'components/navigation/PageTopBar';
import { Button } from 'components/ui/button';
import { Empty, EmptyDescription, EmptyHeader } from 'components/ui/empty';
import { Progress } from 'components/ui/progress';
import { Skeleton } from 'components/ui/skeleton';
import useScrollRestoration from 'hooks/useScrollRestoration';
import posthog from 'posthog-js';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CategoryItemDTO } from 'services/api/models/CategoryDTO';
import onArticleSelected from 'utils/ContentUtils';

export default function InfoCategoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug } = useParams();
  const location = useLocation();
  const scrollableRef = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollableRef);

  const { data: page, isPending, isError } = useCategoryPage(slug as string);
  const bookmark = useBookmarkCategory(slug as string);

  // The chips name the themes and sections of the page: selecting one shows
  // only that one, selecting it again (or its cross) shows everything. The
  // selection lives in the history entry, next to the scroll position, so
  // coming back from an article or from another category finds the page as
  // it was left (the route mounts one instance per category, see App).
  const [selectedChip, setSelectedChip] = useState<number | null>(location.state?.chip ?? null);
  const toggleChip = (id: number) => {
    const next = selectedChip === id ? null : id;
    posthog.capture('info.category_filtered', { category_slug: slug, selected: next !== null });
    setSelectedChip(next);
    navigate(location.pathname + location.search, { replace: true, state: { ...location.state, chip: next } });
  };

  const toggleBookmark = () => {
    if (!page) return;
    const next = !page.is_bookmarked;
    posthog.capture('info.category_bookmarked', { category_slug: slug, bookmarked: next });
    bookmark.mutate(next);
  };

  const back = () => {
    posthog.capture('info.category_closed', { category_slug: slug });
    navigate(-1);
  };

  const chips = (page?.items ?? []).flatMap((item: CategoryItemDTO) =>
    item.type === 'category' && item.presentation !== 'link' ? [item.category] : [],
  );
  const parentName = page && page.path.length > 1 ? page.path[page.path.length - 2].name : t('menu.info');
  const startHere = page?.start_here ?? undefined;
  const openStartHere = () => {
    if (page && startHere) {
      onArticleSelected(startHere, 0, navigate, dispatch, {
        opened_on_page: 'info-start-here',
        found_using_category: page.slug,
      });
    }
  };
  const bookmarkLabel = t(page?.is_bookmarked ? 'infoPage.structure.unbookmark' : 'infoPage.structure.bookmark');

  return (
    <Page>
      <PageTopBar title={parentName} backButton onBackButtonClicked={back} />
      {/* The cover bleeds edge to edge above the content column, like an article's */}
      <div
        className="flex min-h-0 w-full grow flex-col overflow-y-auto *:shrink-0"
        ref={scrollableRef}
        data-testid="category-page"
      >
        {page?.cover_image_url && (
          <img src={page.cover_image_url} alt="" className="h-50 w-full object-cover" data-testid="category-cover" />
        )}
        <div className="mx-auto flex w-full max-w-content flex-col gap-7 px-2 pt-2 pb-8">
          {isPending ? (
            <>
              <Skeleton className="h-9 w-3/5" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </>
          ) : isError ? (
            <Empty>
              <EmptyHeader>
                <EmptyDescription>{t('loginRegister.errors.errorOccurred')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            page && (
              <>
                <header className="flex flex-col gap-2 pt-3">
                  <CategoryIconBox category={page} size="lg" />
                  <div className="flex items-center justify-between gap-3">
                    <h2>{page.name}</h2>
                    <Button
                      variant={page.is_bookmarked ? 'default' : 'outline'}
                      size="icon-lg"
                      data-testid="bookmark-button"
                      aria-pressed={page.is_bookmarked}
                      aria-label={bookmarkLabel}
                      title={bookmarkLabel}
                      onClick={toggleBookmark}
                    >
                      {page.is_bookmarked ? <StarFilled /> : <StarOutlined />}
                    </Button>
                  </div>
                  {page.description && <p className="text-muted-foreground">{page.description}</p>}
                  {page.show_count && (
                    <span className="text-sm text-muted-foreground">
                      {t('infoPage.structure.articles', { count: page.post_count })}
                      {page.read_count > 0 && ` · ${t('infoPage.structure.read', { count: page.read_count })}`}
                    </span>
                  )}
                  {page.show_progress && page.show_count && page.read_count > 0 && (
                    <Progress value={page.read_count} max={page.post_count} />
                  )}
                </header>
                {startHere && !startHere.read && (
                  <div
                    className="flex cursor-pointer flex-col gap-1 rounded-lg bg-inverse p-3.5 text-inverse-foreground"
                    data-testid="start-here"
                    onClick={openStartHere}
                    onKeyDown={e => {
                      if (e.key === 'Enter') openStartHere();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <small className="text-xs font-semibold tracking-wider text-primary uppercase">
                      {t('infoPage.structure.startHere')}
                    </small>
                    <span className="text-lg">{startHere.title}</span>
                  </div>
                )}
                {chips.length > 1 && (
                  <div className="flex flex-wrap gap-2.5">
                    {chips.map(chip => (
                      <FilterChip
                        key={chip.id}
                        selected={selectedChip === chip.id}
                        count={chip.show_count || chip.kind === 'section' ? chip.post_count : undefined}
                        data-testid={`chip-${chip.slug}`}
                        onClick={() => toggleChip(chip.id)}
                      >
                        {chip.name}
                      </FilterChip>
                    ))}
                  </div>
                )}
                <CategoryPageItems page={page} selectedId={selectedChip} />
                <AskMidwifeCard source={`info-category:${page.slug}`} />
              </>
            )
          )}
        </div>
      </div>
    </Page>
  );
}
